<script lang="ts">
  import { fly } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import { toast, type ToastVariant } from "$lib/stores/toast.svelte";
  import { cn } from "$lib/utils/cn";
  import * as m from "$lib/paraglide/messages";

  const toneClass: Record<ToastVariant, string> = {
    info: "border-(--surface-border) bg-(--surface-bg-elev)",
    success: "border-[oklch(58%_0.18_145)]/30 bg-(--surface-bg-elev)",
    warning: "border-[oklch(68%_0.18_65)]/30 bg-(--surface-bg-elev)",
    danger: "border-(--surface-danger)/40 bg-(--surface-bg-elev)",
  };

  const accentBar: Record<ToastVariant, string> = {
    info: "bg-(--surface-accent)",
    success: "bg-[oklch(58%_0.18_145)]",
    warning: "bg-[oklch(68%_0.18_65)]",
    danger: "bg-(--surface-danger)",
  };

  const toneLabel: Record<ToastVariant, () => string> = {
    info: () => m.toast_tone_info(),
    success: () => m.toast_tone_success(),
    warning: () => m.toast_tone_warning(),
    danger: () => m.toast_tone_error(),
  };
</script>

<div
  class="pointer-events-none fixed inset-x-0 top-2 z-60 flex flex-col items-center gap-2 px-4 sm:bottom-4 sm:top-auto sm:items-end sm:px-6"
>
  {#each toast.items as item (item.id)}
    <div
      role={item.variant === "danger" ? "alert" : "status"}
      aria-live={item.variant === "danger" ? "assertive" : "polite"}
      aria-atomic="true"
      class={cn(
        "pointer-events-auto relative flex w-full max-w-sm overflow-hidden rounded-lg border shadow-popover",
        toneClass[item.variant],
      )}
      transition:fly={{ y: -20, duration: 220, easing: quintOut }}
    >
      <span class={cn("w-1 shrink-0", accentBar[item.variant])} aria-hidden="true"></span>
      <div class="flex grow flex-col gap-0.5 px-4 py-3">
        <p class="text-sm font-medium leading-snug">
          <span class="sr-only">{toneLabel[item.variant]()}: </span>
          {item.title}{#if item.count > 1}
            <span class="text-(--surface-fg-muted)"> ×{item.count}</span>
          {/if}
        </p>
        {#if item.description}
          <p class="text-sm leading-snug text-(--surface-fg-muted)">{item.description}</p>
        {/if}
      </div>
      <button
        type="button"
        class="self-start p-2 text-(--surface-fg-subtle) hover:text-(--surface-fg)"
        aria-label={m.toast_dismiss()}
        onclick={() => toast.dismiss(item.id)}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  {/each}
</div>
